import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Building2, ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent, ImageWithSkeleton, cn } from "@cliently/ui";
import { api } from "../lib/api";
import { ALL_CLIENTS_KEY, useOrgStore } from "../stores/org.store";
import { useOrganizations } from "../hooks/use-organizations";
import { resolveOrganizationMediaUrl } from "../lib/organization-media-url";
import {
  BOOKING_PRESENTATION_CARD_MAX_HEIGHT_PX,
  BOOKING_PRESENTATION_CARD_MAX_WIDTH_PX,
  BOOKING_PRESENTATION_CARD_MEDIA_HEIGHT_PX,
  BookingServicePresentationCard,
} from "../components/booking/BookingServicePresentationCard";
import type { PortalServiceItem } from "../types/portal-service-item";
import type { ClientOrgMembership } from "@cliently/types";
import { EmptyIllustration } from "../assets/undraw/empty";

type ServiceEntry = PortalServiceItem & { orgId: string; orgName: string };



export const ServicesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeClientId = useOrgStore((s) => s.activeClientId);
  const isAllProsMode = activeClientId === ALL_CLIENTS_KEY;
  const { data: organizations = [], isLoading: orgsLoading } =
    useOrganizations();

  const activeOrg = useMemo(
    () => organizations.find((o) => o.clientId === activeClientId),
    [organizations, activeClientId],
  );

  const singleQuery = useQuery({
    queryKey: ["portal-service-items"],
    queryFn: async () => {
      const { data } = await api.get("/portal/service-items");
      return data as PortalServiceItem[];
    },
    enabled: !isAllProsMode,
    staleTime: 1000 * 60 * 10,
  });

  const aggregateQueries = useQueries({
    queries: organizations.map((org) => ({
      queryKey: ["portal-aggregate-service-items", org.orgId],
      queryFn: async () => {
        const { data } = await api.get("/portal/aggregate/service-items", {
          params: { orgId: org.orgId },
        });
        const items = data as PortalServiceItem[];
        return items.map(
          (item): ServiceEntry => ({
            ...item,
            orgId: org.orgId,
            orgName: org.orgName,
          }),
        );
      },
      staleTime: 1000 * 60 * 10,
      enabled: isAllProsMode && organizations.length > 0,
    })),
  });

  const services: ServiceEntry[] = useMemo(() => {
    if (!isAllProsMode) {
      const items = singleQuery.data ?? [];
      const oid = activeOrg?.orgId ?? "";
      const oname = activeOrg?.orgName ?? "";
      return items.map((item) => ({
        ...item,
        orgId: oid,
        orgName: oname,
      }));
    }
    return aggregateQueries.flatMap((q) => q.data ?? []);
  }, [
    isAllProsMode,
    singleQuery.data,
    aggregateQueries,
    activeOrg?.orgId,
    activeOrg?.orgName,
  ]);

  const groupedByOrg = useMemo(() => {
    const m = new Map<string, ServiceEntry[]>();
    for (const s of services) {
      const key = s.orgId || "—";
      const list = m.get(key) ?? [];
      list.push(s);
      m.set(key, list);
    }
    return Array.from(m.entries())
      .map(([orgId, items]) => {
        const first = items[0];
        return {
          orgId,
          orgName: first?.orgName ?? orgId,
          items: [...items].sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
          ),
        };
      })
      .sort((a, b) =>
        a.orgName.localeCompare(b.orgName, undefined, { sensitivity: "base" }),
      );
  }, [services]);
  const [slideByOrg, setSlideByOrg] = useState<Record<string, number>>({});

  const orgById = useMemo(() => {
    const map = new Map<string, ClientOrgMembership>();
    for (const o of organizations) {
      map.set(o.orgId, o);
    }
    return map;
  }, [organizations]);

  const isLoading = isAllProsMode
    ? orgsLoading || aggregateQueries.some((q) => q.isLoading)
    : singleQuery.isLoading;

  const handleBook = (entry: ServiceEntry) => {
    const params = new URLSearchParams();
    params.set("from", "services");
    params.set("serviceItemId", entry.id);
    if (isAllProsMode && entry.orgId) {
      params.set("orgId", entry.orgId);
    }
    navigate(`/appointments?${params.toString()}`);
  };

  useEffect(() => {
    if (groupedByOrg.length === 0) return;
    const interval = globalThis.setInterval(() => {
      setSlideByOrg((prev) => {
        const next = { ...prev };
        for (const group of groupedByOrg) {
          const totalSlides = Math.ceil(group.items.length / 4);
          if (totalSlides <= 1) {
            next[group.orgId] = 0;
            continue;
          }
          const current = next[group.orgId] ?? 0;
          next[group.orgId] = (current + 1) % totalSlides;
        }
        return next;
      });
    }, 10000);
    return () => globalThis.clearInterval(interval);
  }, [groupedByOrg]);

  return (
    <div className="space-y-6 pb-8">
      <div>
        <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
          {t("services.title")}
        </h1>
        <p className="text-foreground/80 mt-1 text-sm">{t("services.subtitle")}</p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="border-border/50 mx-auto flex min-h-0 w-full flex-col overflow-hidden rounded-2xl border bg-muted/30"
              style={{
                height: BOOKING_PRESENTATION_CARD_MAX_HEIGHT_PX,
                maxWidth: BOOKING_PRESENTATION_CARD_MAX_WIDTH_PX,
              }}
            >
              <div
                className="w-full shrink-0 animate-pulse bg-muted"
                style={{ height: BOOKING_PRESENTATION_CARD_MEDIA_HEIGHT_PX }}
              />
              <div className="flex min-h-0 flex-1 flex-col p-4">
                <div className="space-y-2.5">
                  <div className="bg-muted-foreground/20 h-4 w-4/5 max-w-48 rounded" />
                  <div className="bg-muted-foreground/12 h-3 w-full rounded" />
                </div>
                <div className="border-border/40 mt-auto flex justify-between border-t pt-3">
                  <div className="bg-muted-foreground/12 h-3 w-14 rounded" />
                  <div className="bg-muted-foreground/20 h-5 w-16 rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : services.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-14 text-center">
            <EmptyIllustration size={120} className="opacity-90" />
            <p className="text-muted-foreground max-w-md text-sm">
              {t("services.empty")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {groupedByOrg.map(({ orgId, orgName, items }) => {
            const membership = orgById.get(orgId);
            const orgImage = membership
              ? resolveOrganizationMediaUrl(
                  membership.orgProfilePhoto ?? membership.orgLogo,
                )
              : null;
            const chunks: ServiceEntry[][] = [];
            for (let i = 0; i < items.length; i += 4) {
              chunks.push(items.slice(i, i + 4));
            }
            const useSlider = isAllProsMode && items.length > 4;
            const totalSlides = chunks.length;
            const rawSlide = slideByOrg[orgId] ?? 0;
            const currentSlide = totalSlides > 0 ? rawSlide % totalSlides : 0;
            const visibleItems = useSlider
              ? (chunks[currentSlide] ?? [])
              : items;

            return (
            <section key={orgId} className="space-y-3">
              {orgId !== "—" ? (
                <div className="flex items-center gap-2.5 px-0.5">
                  {orgImage ? (
                    <ImageWithSkeleton
                      src={orgImage}
                      alt={orgName}
                      className="h-9 w-9 shrink-0 rounded-lg border border-border/60 bg-muted"
                    />
                  ) : (
                    <div
                      className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-primary/15"
                      aria-hidden
                    >
                      <Building2 className="h-4 w-4" />
                    </div>
                  )}
                  <h2 className="text-foreground min-w-0 text-sm font-semibold tracking-tight">
                    {orgName}
                  </h2>
                </div>
              ) : null}
              {useSlider && totalSlides > 1 ? (
                <div className="flex items-center justify-end gap-1">
                  <button
                    type="button"
                    aria-label="Services précédents"
                    className="text-muted-foreground hover:text-foreground inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/70 bg-background"
                    onClick={() =>
                      setSlideByOrg((prev) => ({
                        ...prev,
                        [orgId]:
                          currentSlide === 0 ? totalSlides - 1 : currentSlide - 1,
                      }))
                    }
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="Services suivants"
                    className="text-muted-foreground hover:text-foreground inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/70 bg-background"
                    onClick={() =>
                      setSlideByOrg((prev) => ({
                        ...prev,
                        [orgId]: (currentSlide + 1) % totalSlides,
                      }))
                    }
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              ) : null}
              <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {visibleItems.map((service) => (
                  <BookingServicePresentationCard
                    key={`${service.orgId}-${service.id}`}
                    service={service}
                    bookLabel={t("booking.title")}
                    onBook={() => handleBook(service)}
                  />
                ))}
              </div>
              {useSlider && totalSlides > 1 ? (
                <div className="flex items-center justify-center gap-1.5 pt-1">
                  {chunks.map((chunk, idx) => (
                    <button
                      key={chunk[0]?.id ?? `${orgId}-slide-${idx}`}
                      type="button"
                      aria-label={`Aller à la page ${idx + 1} des services`}
                      className={cn(
                        "h-1.5 rounded-full transition-all",
                        idx === currentSlide
                          ? "bg-primary w-5"
                          : "bg-muted-foreground/35 hover:bg-muted-foreground/60 w-1.5",
                      )}
                      onClick={() =>
                        setSlideByOrg((prev) => ({ ...prev, [orgId]: idx }))
                      }
                    />
                  ))}
                </div>
              ) : null}
            </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
